import { useState } from "react";
import { Loader2, Camera, Save, ShieldCheck, KeyRound, User as UserIcon, Mail as MailIcon, Phone as PhoneIcon, Briefcase, Building2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import tr from "@/i18n/tr";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function initials(name) {
    if (!name) return "OMB";
    return name.split(" ").filter(Boolean).slice(0, 2).map(n => n[0]?.toUpperCase()).join("");
}

function Row({ label, Icon, children }) {
    return (
        <div className="space-y-1.5">
            <label className="block text-[12.5px] font-semibold text-foreground">{label}</label>
            <div className="relative">
                {Icon && <Icon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />}
                {children}
            </div>
        </div>
    );
}

export default function ProfilePage() {
    const { user, updateUser } = useAuth();
    const [form, setForm] = useState({
        full_name: user?.full_name || "",
        phone: user?.phone || "",
        title: user?.title || "",
        department: user?.department || "",
    });
    const [saving, setSaving] = useState(false);

    const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
    const [pwdSaving, setPwdSaving] = useState(false);

    const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSave = async (ev) => {
        ev.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.put("/users/me", form);
            updateUser(data);
            toast.success(tr.profile.savedSuccess);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Güncelleme başarısız.");
        } finally {
            setSaving(false);
        }
    };

    const onChangePassword = async (ev) => {
        ev.preventDefault();
        if (pwd.next.length < 8) return toast.error(tr.validation.passwordMin);
        if (pwd.next !== pwd.confirm) return toast.error(tr.validation.passwordsMismatch);
        setPwdSaving(true);
        try {
            await api.post("/users/me/change-password", {
                current_password: pwd.current,
                new_password: pwd.next,
            });
            toast.success(tr.profile.passwordChanged);
            setPwd({ current: "", next: "", confirm: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Şifre güncellenemedi.");
        } finally {
            setPwdSaving(false);
        }
    };

    const inputCls = "h-11 w-full rounded-xl border border-border bg-background pl-10 pr-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40 transition";
    const inputClsNoIcon = "h-11 w-full rounded-xl border border-border bg-background px-3.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand/40 transition";

    return (
        <div className="space-y-6 animate-fade-in">
            <div>
                <h1 className="text-[26px] font-bold tracking-tight">{tr.profile.title}</h1>
                <p className="text-[13.5px] text-muted-foreground mt-1">{tr.profile.subtitle}</p>
            </div>

            {/* Profile header card */}
            <div className="card-elevated p-6">
                <div className="flex flex-col sm:flex-row items-start gap-6">
                    <div className="relative shrink-0">
                        <Avatar className="h-24 w-24 border-4 border-background shadow-card">
                            <AvatarImage src={user?.avatar_url || undefined} alt={user?.full_name} />
                            <AvatarFallback className="bg-[hsl(var(--brand))] text-white text-2xl font-bold">
                                {initials(user?.full_name)}
                            </AvatarFallback>
                        </Avatar>
                        <button className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-[hsl(var(--brand))] text-white shadow-brand hover:bg-[hsl(var(--brand-hover))]" aria-label="Fotoğrafı değiştir">
                            <Camera className="h-4 w-4" />
                        </button>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-[20px] font-bold tracking-tight">{user?.full_name}</h2>
                            <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))] px-2 py-0.5 text-[11px] font-semibold">
                                <ShieldCheck className="h-3 w-3" />
                                {user?.role === "super_admin" ? "Süper Yönetici" : user?.role === "admin" ? "Yönetici" : user?.role === "manager" ? "Departman Yöneticisi" : user?.role === "client" ? "Müşteri" : "Personel"}
                            </span>
                        </div>
                        <div className="mt-1 text-[13px] text-muted-foreground">{user?.title} · {user?.department}</div>
                        <div className="mt-1 text-[13px] text-muted-foreground">{user?.email}</div>
                        <div className="mt-4 flex flex-wrap gap-4 text-[12px] text-muted-foreground">
                            <div>
                                <span className="font-semibold text-foreground">{tr.profile.joined}: </span>
                                {user?.created_at ? new Date(user.created_at).toLocaleDateString("tr-TR") : "—"}
                            </div>
                            {user?.last_login_at && (
                                <div>
                                    <span className="font-semibold text-foreground">{tr.profile.lastLogin}: </span>
                                    {new Date(user.last_login_at).toLocaleString("tr-TR")}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Tabs defaultValue="personal" className="space-y-4">
                <TabsList className="bg-muted/60 p-1 rounded-xl">
                    <TabsTrigger value="personal" data-testid="tab-personal" className="data-[state=active]:bg-background data-[state=active]:shadow-card rounded-lg">
                        {tr.profile.personalInfo}
                    </TabsTrigger>
                    <TabsTrigger value="security" data-testid="tab-security" className="data-[state=active]:bg-background data-[state=active]:shadow-card rounded-lg">
                        {tr.profile.security}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="personal">
                    <form onSubmit={onSave} className="card-elevated p-6 space-y-5" data-testid="profile-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Row label={tr.common.fullName} Icon={UserIcon}>
                                <input value={form.full_name} onChange={onChange("full_name")} className={inputCls} placeholder="Ad Soyad" data-testid="profile-full-name" />
                            </Row>
                            <Row label={tr.common.email} Icon={MailIcon}>
                                <input value={user?.email || ""} disabled className={`${inputCls} bg-muted/60 cursor-not-allowed`} />
                            </Row>
                            <Row label={tr.common.phone} Icon={PhoneIcon}>
                                <input value={form.phone} onChange={onChange("phone")} className={inputCls} placeholder="+90 555 000 00 00" data-testid="profile-phone" />
                            </Row>
                            <Row label={tr.common.title} Icon={Briefcase}>
                                <input value={form.title} onChange={onChange("title")} className={inputCls} placeholder="Ör: İçerik Uzmanı" data-testid="profile-title" />
                            </Row>
                            <div className="md:col-span-2">
                                <Row label={tr.common.department} Icon={Building2}>
                                    <input value={form.department} onChange={onChange("department")} className={inputCls} placeholder="Ör: Yaratıcı Grup" data-testid="profile-department" />
                                </Row>
                            </div>
                        </div>
                        <div className="flex justify-end pt-2">
                            <button type="submit" disabled={saving} className="btn-brand h-11" data-testid="profile-save">
                                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                {tr.common.save}
                            </button>
                        </div>
                    </form>
                </TabsContent>

                <TabsContent value="security">
                    <form onSubmit={onChangePassword} className="card-elevated p-6 space-y-5" data-testid="password-form">
                        <div className="flex items-start gap-3 mb-2">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--brand))]/10 text-[hsl(var(--brand))]">
                                <KeyRound className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="text-[15px] font-semibold">{tr.profile.changePassword}</div>
                                <div className="text-[12.5px] text-muted-foreground">En az 8 karakter olmalıdır. Güçlü bir şifre seçin.</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-1.5">
                                <label className="block text-[12.5px] font-semibold">{tr.common.currentPassword}</label>
                                <input type="password" value={pwd.current} onChange={(e) => setPwd({...pwd, current: e.target.value})} className={inputClsNoIcon} data-testid="pwd-current" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="block text-[12.5px] font-semibold">{tr.common.newPassword}</label>
                                <input type="password" value={pwd.next} onChange={(e) => setPwd({...pwd, next: e.target.value})} className={inputClsNoIcon} data-testid="pwd-new" />
                            </div>
                            <div className="space-y-1.5">
                                <label className="block text-[12.5px] font-semibold">{tr.common.confirmPassword}</label>
                                <input type="password" value={pwd.confirm} onChange={(e) => setPwd({...pwd, confirm: e.target.value})} className={inputClsNoIcon} data-testid="pwd-confirm" />
                            </div>
                        </div>
                        <div className="flex justify-end">
                            <button type="submit" disabled={pwdSaving} className="btn-brand h-11" data-testid="pwd-save">
                                {pwdSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                                {tr.profile.changePassword}
                            </button>
                        </div>
                    </form>
                </TabsContent>
            </Tabs>
        </div>
    );
}
